import React from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import cn from 'classnames';

import { IconColor } from 'widget/shared/icons/BaseIcon';
import SendMessageIcon from 'widget/shared/icons/SendMessageIcon';
import EmojiPickerIcon from 'widget/shared/icons/EmojiPickerIcon';
import EmojiPicker from 'widget/components/EmojiPicker';
import Typography from 'widget/shared/ui/Typography';
import {
  TypographySize,
  TypographyWeight,
  TypographyColor
} from 'widget/shared/ui/Typography/types';
import type { AttachmentsStore } from 'widget/stores/AttachmentsStore';
import { WidgetTexts } from 'widget/consts/texts';

import type { EmojiPickerState } from '../useEmojiPicker';

import AddFilesBtn from './AddFilesBtn';
import { AttachmentsList } from './AttachmentsList';
import styles from './FormBody.module.scss';

const BUTTONS_HEIGHT = 36;

export type FormBodyProps = {
  message: string;
  error?: string | null;
  onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onChangeMessage: (text: string) => void;
  onSubmitMessage: () => void;
  disabled: boolean;
  className?: string;
  maxRows?: number;
  attachmentsStore?: AttachmentsStore;
  emojiPicker?: EmojiPickerState;
};

const FormBody: React.FC<FormBodyProps> = ({
  message,
  onKeyDown,
  onPaste,
  onChangeMessage,
  onSubmitMessage,
  disabled,
  error,
  className,
  maxRows,
  attachmentsStore,
  emojiPicker
}) => {
  const formRef = React.useRef<HTMLDivElement>(null);
  const [buttonsOffset, setButtonsOffset] = React.useState<number | undefined>(
    undefined
  );
  const [borderWidth, setBorderWidth] = React.useState<number | undefined>(
    undefined
  );

  React.useLayoutEffect(() => {
    if (formRef.current) {
      setButtonsOffset(
        (formRef.current.getBoundingClientRect().height - BUTTONS_HEIGHT) / 2
      );
    }
  }, []);

  React.useEffect(() => {
    if (formRef.current) {
      setBorderWidth(
        parseFloat(getComputedStyle(formRef.current).borderBottomWidth) || 0
      );
    }
  }, []);

  const handleSubmit = React.useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      onSubmitMessage();
    },
    [onSubmitMessage]
  );

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChangeMessage(event.target.value);
    },
    [onChangeMessage]
  );

  return (
    <form className={cn(styles['form'], className)} onSubmit={handleSubmit}>
      {attachmentsStore && (
        <AttachmentsList
          attachmentsStore={attachmentsStore}
          className={styles['form__attachments']}
        />
      )}
      <div className={styles['form__input-wrapper']}>
        {emojiPicker?.isOpen && (
          <div
            ref={emojiPicker.containerRef}
            className={styles['form__emoji-picker']}
          >
            <EmojiPicker onEmojiSelect={emojiPicker.onSelect} />
          </div>
        )}
        <div ref={formRef} className={styles['form__container']}>
          <TextareaAutosize
            ref={emojiPicker?.inputRef}
            value={message}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onBlur={emojiPicker?.onInputBlur}
            placeholder={WidgetTexts.messageForm.messages.placeholder}
            maxRows={message ? maxRows : 1}
            className={styles['form__textarea']}
          />
          <div
            className={styles['form__buttons']}
            style={
              buttonsOffset !== undefined && borderWidth !== undefined
                ? {
                    bottom: `${buttonsOffset - borderWidth / 2}px`,
                    right: `${buttonsOffset - borderWidth}px`
                  }
                : undefined
            }
          >
            {emojiPicker && (
              <button
                ref={emojiPicker.buttonRef}
                type="button"
                className={cn(
                  styles['form__button'],
                  styles['form__emoji-trigger']
                )}
                aria-label={WidgetTexts.messageForm.emojiPicker.label}
                onMouseDown={emojiPicker.onButtonMouseDown}
                onClick={emojiPicker.onToggle}
              >
                <EmojiPickerIcon color={IconColor.Inherit} />
              </button>
            )}
            {attachmentsStore && (
              <AddFilesBtn
                className={styles['form__button']}
                onFilesSelect={attachmentsStore.addFiles}
              />
            )}
            <button
              className={cn(styles['form__button'], styles['form__submit'])}
              type="submit"
              aria-label={WidgetTexts.messageForm.messages.label}
              disabled={disabled}
            >
              <SendMessageIcon />
            </button>
          </div>
        </div>
      </div>
      {error && (
        <Typography
          size={TypographySize.xs}
          weight={TypographyWeight.semiBold}
          color={TypographyColor.error}
          className={styles['form__error']}
        >
          {error}
        </Typography>
      )}
    </form>
  );
};

export default React.memo(FormBody);
